import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useModal } from '../contexts/ModalContext'
import { useResetPassword } from '../hooks/auth/useResetPassword'
import { ScreenPath } from '../navigation'
import { logError } from '../util/errorLogger'
import SimplePageLayout from '../ui/layout/SimplePageLayout'
import Button from '../ui/primitives/Button'
import TextField from '../ui/primitives/TextField'
import styles from '../ui/auth/AuthForm.module.css'

interface ForgotPasswordForm {
  email: string
}

export default function ForgotPasswordScreen() {
  const navigate = useNavigate()
  const { showSnackbar } = useModal()
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ForgotPasswordForm>()
  const { mutateAsync: resetPassword, isPending } = useResetPassword()

  async function onSubmit(data: ForgotPasswordForm) {
    try {
      await resetPassword(data.email)
      showSnackbar('reset-success', 'Check your email for password reset instructions', 'success')
      navigate(ScreenPath.login, { replace: true })
    } catch (e) {
      void logError(e, { screen: 'ForgotPassword' })
      showSnackbar('reset-error', 'Sorry! There was a problem sending the reset email', 'error')
    }
  }

  return (
    <SimplePageLayout title="Reset password" backTo={ScreenPath.login}>
      <div className={styles.card}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <p className={styles.hint}>
            Enter your email and we&apos;ll send instructions to reset your password.
          </p>
          <TextField
            label="Email"
            type="email"
            error={errors.email?.message}
            {...register('email', { required: 'Email is required' })}
          />
          <div className={styles.actions}>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Sending…' : 'Send reset instructions'}
            </Button>
            <p className={styles.linkRow}>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => navigate(ScreenPath.login, { replace: true })}
              >
                Back to log in
              </button>
            </p>
          </div>
        </form>
      </div>
    </SimplePageLayout>
  )
}
